import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

const ADMIN_SESSION_KEY = "dds_admin_session";
const ADMIN_MODE_KEY = "dds_admin_mode";

// Node prerender process has no `window`/`localStorage`. Guard every access
// so AdminProvider can mount during build-time renderToString() without
// throwing — admin state simply resolves to "logged out" server-side, which
// is correct (a prerendered page should never look pre-authenticated).
const IS_BROWSER = typeof window !== "undefined" && typeof window.localStorage !== "undefined";

interface AdminContextType {
  isAdmin: boolean;
  adminMode: boolean;
  toggleAdminMode: () => void;
  setAdminMode: (on: boolean) => void;
  login: (username: string, role: string) => void;
  logout: () => void;
  checkAuth: () => boolean;
}

const AdminContext = createContext<AdminContextType | null>(null);

function sessionValid(): boolean {
  if (!IS_BROWSER) return false;
  try {
    const session = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!session) return false;
    const parsed = JSON.parse(session);
    return Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function AdminProvider({ children }: { children: ReactNode }) {
  // Initialise synchronously from localStorage so the very first render already
  // knows whether the user is an admin. (Doing this in an effect caused a race:
  // route guards that redirect on `!isAdmin` fired before the effect resolved.)
  const [isAdmin, setIsAdmin] = useState<boolean>(() => sessionValid());
  const [adminMode, setAdminModeState] = useState<boolean>(() => {
    if (!sessionValid()) return false;
    const stored = IS_BROWSER ? localStorage.getItem(ADMIN_MODE_KEY) : null;
    // Admin mode defaults ON while logged in, so the admin immediately sees the
    // editable mirror of the site.
    return stored === null ? true : stored === "1";
  });

  const checkAuth = useCallback((): boolean => sessionValid(), []);

  const setAdminMode = useCallback((on: boolean) => {
    setAdminModeState(on);
    if (IS_BROWSER) localStorage.setItem(ADMIN_MODE_KEY, on ? "1" : "0");
  }, []);

  const login = useCallback((username: string, role: string) => {
    const session = { username, role, timestamp: Date.now() };
    if (IS_BROWSER) {
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
      localStorage.setItem(ADMIN_MODE_KEY, "1");
    }
    setIsAdmin(true);
    setAdminModeState(true);
  }, []);

  const logout = useCallback(() => {
    if (IS_BROWSER) {
      localStorage.removeItem(ADMIN_SESSION_KEY);
      localStorage.removeItem(ADMIN_MODE_KEY);
    }
    setIsAdmin(false);
    setAdminModeState(false);
  }, []);

  const toggleAdminMode = useCallback(() => {
    if (!isAdmin) return;
    setAdminMode(!adminMode);
  }, [isAdmin, adminMode, setAdminMode]);

  return (
    <AdminContext.Provider
      value={{ isAdmin, adminMode, toggleAdminMode, setAdminMode, login, logout, checkAuth }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error("useAdmin must be used within AdminProvider");
  return ctx;
}

export { ADMIN_SESSION_KEY };
